import { useState, useEffect, useCallback } from 'react'
import type { Share, CreateShareRequest } from '../types'

export function useShares(filters?: { seed?: string; target?: string }) {
  const [shares, setShares] = useState<Share[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchShares = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (filters?.seed) params.set('seed', filters.seed)
      if (filters?.target) params.set('target', filters.target)
      const qs = params.toString()
      const res = await fetch(`/api/shares${qs ? `?${qs}` : ''}`)
      if (!res.ok) throw new Error('Failed to fetch shares')
      const data = await res.json()
      setShares(data.shares || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [filters?.seed, filters?.target])

  useEffect(() => {
    fetchShares()
  }, [fetchShares])

  const createShare = async (req: CreateShareRequest): Promise<Share> => {
    const res = await fetch('/api/shares', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    })
    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.error || 'Failed to create share')
    }
    const share = await res.json()
    await fetchShares()
    return share
  }

  const updateShare = async (id: number, updates: Partial<Share> & { expiresAt?: string }) => {
    const res = await fetch(`/api/shares/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    })
    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.error || 'Failed to update share')
    }
    await fetchShares()
    return res.json()
  }

  const deleteShare = async (id: number) => {
    const res = await fetch(`/api/shares/${id}`, { method: 'DELETE' })
    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.error || 'Failed to delete share')
    }
    await fetchShares()
  }

  const extendShare = async (id: number, expiresAt: string) => {
    return updateShare(id, { expiresAt })
  }

  return { shares, loading, error, refetch: fetchShares, createShare, updateShare, deleteShare, extendShare }
}

export function useShare(id: string) {
  const [share, setShare] = useState<Share | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchShare = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`/api/shares/${id}`)
      if (!res.ok) {
        if (res.status === 404) { setShare(null); return }
        throw new Error('Failed to fetch share')
      }
      setShare(await res.json())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchShare()
  }, [fetchShare])

  return { share, loading, error, refetch: fetchShare }
}
